
import { Vue, Options } from 'vue-class-component'
import FormField from '@/components/form/FormField.vue'
import { Prop, Watch } from 'vue-property-decorator'
import { maska } from 'maska'
import { IFormDetail, IFormLIFFDetail, IFormLIFFImageDetail, IMedia } from '@/utils/types'
import FormLIFFField from '@/components/form/FormLIFFField.vue'
import { ASSETS_FORM_TYPE } from '@/utils/constants'
import UploadingProgress from '@/components/common/ui/UploadingProgress.vue'
import MediaUploader from '@/components/media-manager/MediaUploader.vue'
import CropperImageSelector from '@/components/tappable-area/selectors/CropperImageSelector.vue'
import UploadApi from '@/api/upload'

@Options({
  components: { FormLIFFField, FormField, MediaUploader, CropperImageSelector, UploadingProgress },
  directives: { maska },
  emits: ['update:modelValue', 'update:formSize', 'update:formImageHeaderUrl'],
})
export default class FormDetail extends Vue {
  @Prop({ default: [] })
  modelValue!: IFormDetail[] | IFormLIFFDetail[]

  @Prop()
  isDeleteFirst!: boolean

  @Prop()
  formType!: string

  @Prop()
  formSize!: string

  @Prop()
  formImageHeaderUrl!: string

  file: File | null = null
  // imageUrl = ''
  errorImageMessage = ''
  cropperImageModalVisible = false
  cropFiles: File[] = []

  get value() {
    return this.modelValue
  }

  set value(value: IFormDetail[] | IFormLIFFDetail[]) {
    this.$emit('update:modelValue', value)
  }

  get currentFormSize() {
    return this.formSize
  }

  set currentFormSize(value: string) {
    this.$emit('update:formSize', value)
  }

  get currentFormImageHeaderUrl() {
    return this.formImageHeaderUrl
  }

  set currentFormImageHeaderUrl(value: string) {
    this.$emit('update:formImageHeaderUrl', value)
  }

  get options() {
    return [
      {
        value: 'compact',
        label: this.$t('label.common.form_liff.size_compact'),
      },
      {
        value: 'tall',
        label: this.$t('label.common.form_liff.size_tall'),
      },
      {
        value: 'full',
        label: this.$t('label.common.form_liff.size_full'),
      },
    ]
  }

  get formDescription() {
    if (this.formType === ASSETS_FORM_TYPE.CHAT) {
      return 'チャット形式で一問一答で回答してもらう内容を設定してください。'
    }
    return 'フォームの表示サイズを選択し、設問を設計してください。'
  }

  get requiredRule() {
    return {
      formName: this.$t('validate.this_field_is_required', {
        placeholder: 'フォームサイズ',
      }),
    }
  }

  get headerImgUrl() {
    let url = ''
    const headerField = (this.value as IFormLIFFDetail[]).find((f) => f.option_type === 'image')
    if (headerField) {
      const item = headerField as IFormLIFFImageDetail
      url = item.img_url || ''
    }
    return url
  }

  onRemoveImage() {
    this.currentFormImageHeaderUrl = ''
  }

  async validateImageWidth(file: File) {
    return new Promise<boolean>((resolve) => {
      // Check file size (10MB limit)
      if (file.size >= 10 * 1024 * 1024) {
        this.errorImageMessage = this.$t('validate.image_size_too_large', {
          placeholder: file.name,
        })
        resolve(false)
        return
      }

      resolve(true)
    })
  }

  async submitFile(file: File) {
    try {
      const uploaded = await UploadApi.uploadFile(file)
      if (uploaded && this.value.length < 10) {
        this.currentFormImageHeaderUrl = uploaded.file_url_org ?? ''
      }
    } catch (error) {
      console.log(error)
    }
  }

  @Watch('file')
  async handleUpload() {
    if (!this.file) {
      return false
    }

    try {
      if (!(await this.validateImageWidth(this.file))) {
        // Show error message for file size validation
        this.$q.notify({
          type: 'negative',
          message: this.errorImageMessage,
          position: 'top',
        })
        this.onCloseModal()
      } else {
        this.cropFiles.push(this.file)
        await this.submitFile(this.file)
        this.file = null
        this.cropFiles = []
      }
    } catch (error) {
      console.log(error)
    }
  }

  onCloseModal() {
    this.cropperImageModalVisible = false
    this.errorImageMessage = ''
    this.file = null
    this.cropFiles = []
  }

  async onCropImage(file) {
    this.cropperImageModalVisible = false
    this.cropFiles.push(file)
    await this.submitFile(file)
    this.onCloseModal()
  }

  async onSelectMedia(file: IMedia) {
    if (file) {
      this.currentFormImageHeaderUrl = file.url || ''
    }
  }
}
